'use client';

import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useMemo } from 'react';

import {
  EventPlanningWrapper,
  Heading,
  Sponsors
} from 'components/atomic/atoms';
import {
  Achievements,
  EventGalleryMobile,
  EventGalleryWeb,
  EventPlanning,
  WhyHafla
} from 'components/atomic/molecules';
import {
  DefaultLayoutOrganism,
  HeaderOrganism
} from 'components/atomic/organism';
import { staticMediaStoreBaseURL } from 'config/common';
import { useIsMobile } from 'helpers/screenResolutionHelper';
import {
  captureGTMEventCTAClick,
  GTM_EVENTS_CTA_NAME,
  PAGE_NAME
} from 'lib/gtm';
import { getPageURL, ROUTE_CONFIG } from 'services/hostPortal.service';

const LOGOS = [
  '/logos/wedding/abb.jpg',
  '/logos/wedding/american-university.jpg',
  '/logos/wedding/bcg.jpg',
  '/logos/wedding/choithrams.jpg',
  '/logos/wedding/emaar.jpg',
  '/logos/wedding/friday.jpg',
  '/logos/wedding/gems.jpg',
  '/logos/wedding/giorgio.jpg',
  '/logos/wedding/hilton.jpg',
  '/logos/wedding/lulu.jpg',
  '/logos/wedding/majid.jpg',
  '/logos/wedding/shuruq.jpg',
  '/logos/wedding/sobha.jpg',
  '/logos/wedding/sony.jpg',
  '/logos/wedding/tiktok.jpg'
];

export default function OtherEventsClient({ eventGallery }) {
  const [isMobile] = useIsMobile();
  const t = useTranslations('otherEventsPage');
  const eventId = 'celebrations';

  // Memoize event plan info to avoid unnecessary parsing
  // Change this:
  const eventPlanInfo = useMemo(() => {
    try {
      // next-intl's t() tries to parse ICU syntax. t.raw() returns the raw value.
      const rawJson = t.raw('eventPlanInfo');

      // If the value is already an object/array in your JSON file,
      // next-intl might return it directly.
      return typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson;
    } catch (e) {
      console.error('Failed to parse eventPlanInfo', e);
      return [];
    }
  }, [t]);

  // Derived banner image logic
  const bannerImageUrl = isMobile
    ? `${staticMediaStoreBaseURL}/mobile-banner/banners/celebrations-events-banner.jpg`
    : `${staticMediaStoreBaseURL}/banners/celebrations-events-banner.jpg`;

  const EventGallery = isMobile ? EventGalleryMobile : EventGalleryWeb;

  return (
    <DefaultLayoutOrganism
      paramData={{
        eventCategory: 'celebrations',
        hideBanner: true,
        bannerTranslate: t
      }}
    >
      <HeaderOrganism heading='Events' />
      <div className='w-full bg-cover bg-center'>
        <div
          className='max-h-40 md:max-h-105 h-full min-h-40 md:min-h-105 flex items-center justify-start bg-nero bg-opacity-50 px-5 md:px-20 bg-no-repeat bg-cover bg-top'
          style={{ backgroundImage: `url(${bannerImageUrl})` }}
        >
          <div className='text-left max-w-52 md:max-w-136 w-full'>
            <Heading
              level={2}
              className='text-2xl md:text-6xl text-nero uppercase md:mb-7 leading-6'
            >
              {t('bannerText')}
            </Heading>
            <Link
              href={getPageURL({
                pageName: ROUTE_CONFIG.EVENTS_LEAD_GENERATION_FORM.label,
                pathParams: { id: eventId }
              })}
              onClick={() => {
                captureGTMEventCTAClick({
                  ctaName: GTM_EVENTS_CTA_NAME.CONSULT_EVENT_EXPERT,
                  pageName: PAGE_NAME.EVENT_LEAD_GEN.label
                });
              }}
            >
              <div className='hidden md:block max-w-63 w-full'>
                <div className='text-black cursor-pointer font-brandon py-3.5 text-center bg-white hover:bg-schedule-btn hover:text-white rounded-md'>
                  {t('buttonText')}
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>
      <Sponsors logos={LOGOS} />
      <EventPlanningWrapper eventId={eventId}>
        <EventPlanning
          eventPlanHeading={'YOU NAME IT, WE DO IT!'}
          eventPlanInfo={eventPlanInfo}
        />
      </EventPlanningWrapper>
      <WhyHafla eventId={eventId} />
      <Achievements />
      <EventGallery
        b2bPage={true}
        eventCategory={eventId}
        eventGallery={eventGallery}
        title='eventGalleryHeadingForOtherEvents'
      />
    </DefaultLayoutOrganism>
  );
}
